/**
 * CME App Router — Capacitor iOS WebView Navigation Guard
 * =========================================================
 * Loaded in theme.liquid <head> as a plain <script> (not module).
 *
 * PROBLEM:
 *   Shopify's New Customer Accounts redirect through
 *   https://shopify.com/authentication/... for login.
 *   Capacitor's WKWebView treats any non-tmafianie.com URL as external
 *   and hands it to Safari via appUrlOpen. The Safari session has
 *   separate cookies → user appears logged out in the app after login.
 *
 * SOLUTION:
 *   1. Intercept every <a> click before it fires.
 *   2. For internal-looking URLs (our domain, /account/*, /pages/vault,
 *      or shopify.com/authentication/*) → force window.location.href.
 *   3. For genuinely external URLs (YouTube, PayPal, socials, checkout)
 *      → let the link propagate normally so Capacitor can open it
 *      externally via appUrlOpen.
 *
 * VAULT FLOW:
 *   - Logged-out user taps Vault → vault gate overlay shows register/login form.
 *   - Login link href is /account/login?return_url=%2Fpages%2Fmusic-vault
 *   - Form submits in WebView → Shopify sets cookie → redirects back to vault.
 *   - Vault gate {% if customer %} evaluates true → overlay hidden → access granted.
 *
 * This file is separate from the inline theme.liquid script so it can be
 * cached independently and updated without touching the layout file.
 */

(function () {
  'use strict';

  // ── Internal path prefixes (relative paths only) ──────────────────────────
  var INTERNAL_PATH_PREFIXES = [
    '/account',
    '/challenge',
    '/password',
    '/pages/',
    '/products/',
    '/collections/',
    '/cart',
    '/music',
    '/beats',
    '/booking',
    '/search',
    '/blogs/',
    '/policies/'
  ];

  // ── Our own hostnames ─────────────────────────────────────────────────────
  var OWN_HOSTS = ['tmafianie.com', 'www.tmafianie.com'];

  // ── Shopify auth flows that MUST stay in the WebView ─────────────────────
  // New Customer Accounts redirects through shopify.com/authentication — if
  // Capacitor opens this in Safari, the resulting session cookie is isolated
  // from the WKWebView and the user sees themselves as logged out in the app.
  var SHOPIFY_AUTH_RE = [
    /shopify\.com\/authentication/i,
    /shopify\.com\/[0-9]+\/account/i,
    /shopify\.com\/account/i,
    /shop\.app/i
  ];

  // ── Explicitly external domains/paths ────────────────────────────────────
  var EXTERNAL_RE = [
    /\/checkout/i,
    /\/checkouts\//i,
    /checkout\.shopify\.com/i,
    /\.myshopify\.com\/checkouts/i,
    /paypal\.com/i,
    /stripe\.com/i,
    /youtube\.com/i,
    /youtu\.be/i,
    /spotify\.com/i,
    /music\.apple\.com/i,
    /instagram\.com/i,
    /tiktok\.com/i,
    /(?:^|\.)x\.com(?:\/|$)/i,
    /twitter\.com/i,
    /gumroad\.com/i,
    /linktr\.ee/i
  ];

  /**
   * Returns true if the given href should be handled inside the WebView.
   * @param {string} href
   * @returns {boolean}
   */
  function isInternal(href) {
    if (!href) return false;
    if (href.charAt(0) === '#') return false; // anchor — not a navigation

    // ── Relative path ─────────────────────────────────────────────────────
    if (href.charAt(0) === '/') {
      // Checkout is always external even on our domain
      if (/^\/checkouts?/.test(href)) return false;
      for (var p = 0; p < INTERNAL_PATH_PREFIXES.length; p++) {
        if (href.indexOf(INTERNAL_PATH_PREFIXES[p]) === 0) return true;
      }
      // Any other relative path on this domain = internal (products, etc.)
      return true;
    }

    var lower = href.toLowerCase();

    // ── Check external patterns first ─────────────────────────────────────
    for (var e = 0; e < EXTERNAL_RE.length; e++) {
      if (EXTERNAL_RE[e].test(lower)) return false;
    }

    // ── Shopify auth → must stay in WebView ──────────────────────────────
    for (var s = 0; s < SHOPIFY_AUTH_RE.length; s++) {
      if (SHOPIFY_AUTH_RE[s].test(lower)) return true;
    }

    // ── Absolute URL — parse hostname ─────────────────────────────────────
    try {
      var parsed = new URL(href, window.location.href);
      var host = parsed.hostname.toLowerCase();

      if (OWN_HOSTS.indexOf(host) > -1) return true;
      if (host === window.location.hostname) return true;
    } catch (err) {
      // Malformed URL — treat as internal to be safe
      return true;
    }

    return false;
  }

  // ── Click interceptor ─────────────────────────────────────────────────────
  document.addEventListener(
    'click',
    function (e) {
      var link = e.target && e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;

      if (isInternal(href)) {
        // If the link has any target attribute, Capacitor may open it
        // externally. Strip the target and force same-page navigation.
        if (link.target && link.target !== '') {
          e.preventDefault();
          window.location.href = href;
          if (typeof console !== 'undefined') {
            console.log('[CME Router] Internal (target override): ' + href);
          }
        }
        // No target → default WKWebView navigation (stays in app)
      } else {
        if (typeof console !== 'undefined') {
          console.log('[CME Router] External (allowed out): ' + href);
        }
        // Let the click propagate. Capacitor appUrlOpen opens it externally.
      }
    },
    true // capture phase — fires before any component handlers
  );

  // ── Account form safeguard ────────────────────────────────────────────────
  // Strip target from any login/register form so its submission + redirect
  // stays within the WebView cookie jar.
  function patchAccountForms() {
    var forms = document.querySelectorAll(
      'form[action*="/account"], form[action*="/challenge"]'
    );
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].target && forms[i].target !== '_self') {
        forms[i].removeAttribute('target');
        if (typeof console !== 'undefined') {
          console.log('[CME Router] Patched form target: ' + forms[i].action);
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAccountForms);
  } else {
    patchAccountForms();
  }

  // Expose for debugging
  window._cmeRouter = { isInternal: isInternal };
})();
